'use client';
import { SectionContainer } from '../component/SectionContainer';
import { MeetingIntroData, TCoreContent } from '../type';
import { useMemo, useState } from 'react';
import { getCMSIntlVal } from '../utils/common';
import { useLocale, useTranslations } from 'use-intl';
import { ChevronDownIcon } from 'tdesign-icons-react';
import { useMedia } from '@ssv-website/hooks';

const FeaturePointItem = ({
  item,
  className = '',
}: {
  item: TCoreContent;
  className: string;
}) => {
  const locale = useLocale();
  const { color, bgStyle, bgImage } = item;
  const title = getCMSIntlVal(item, 'title', locale);
  const desc = getCMSIntlVal(item, 'desc', locale);
  const enDesc = desc.replace(/\/|\\n|\n/g, '\n');
  const descText = locale === 'en' ? enDesc : desc;
  const background = bgImage
    ? `url(${bgImage}) no-repeat center/cover `
    : bgStyle;
  return (
    <div
      className={`p-[56px] flex flex-col min-h-[282px] ${className}`}
      style={{
        background: background,
      }}
    >
      <div
        className={`text-[36px] leading-[1.5] mb-[32px]`}
        style={{ color: color }}
      >
        {title}
      </div>
      <p
        className={`text-[24px] leading-[36px] font-[290] opacity-[0.7] text-nowrap whitespace-pre-line`}
        style={{ color: color }}
      >
        {descText}
      </p>
    </div>
  );
};
const FeaturePointItemMobile = ({
  item,
  className = '',
}: {
  item: TCoreContent;
  className: string;
}) => {
  const locale = useLocale();
  const { color, bgStyle_m, bgImage_m } = item;
  const title = getCMSIntlVal(item, 'title', locale);
  const desc = getCMSIntlVal(item, 'desc', locale);
  const enDesc = desc.replace(/\/|\\n|\n/g, '\n');
  const descText = locale === 'en' ? enDesc : desc;
  const background = bgImage_m
    ? `url(${bgImage_m}) no-repeat center/cover `
    : bgStyle_m;
  return (
    <div
      className={`p-[24px] min-h-[98px] items-center ${className}`}
      style={{
        background: background,
      }}
    >
      <div
        className={`text-[20px] leading-[1.5] mb-2 opacity-80`}
        style={{ color: color }}
      >
        {title}
      </div>
      <p
        className={`text-[14px] leading-[1.5] font-300 opacity-80 whitespace-pre-line`}
        style={{ color: color }}
      >
        {descText}
      </p>
    </div>
  );
};

export default function MeetingIntroSection({
  data = [],
  title,
}: {
  data: MeetingIntroData[];
  title: string;
}) {
  const isMobile = useMedia('mobile');
  const [showMore, setShowMore] = useState(false);
  const t = useTranslations('s2025');
  const locale = useLocale();
  // 数据转换处理
  const translateData = (_data: MeetingIntroData[]) => {
    const info = _data?.[0];
    if (!info) return null;
    const { coreContent } = info || {};
    const tCoreContent: TCoreContent[] = JSON.parse(coreContent || '[]');
    tCoreContent.forEach((item) => {
      // 内部中英文格式化
      item.title = getCMSIntlVal(item, 'title', locale);
      item.desc = getCMSIntlVal(item, 'desc', locale);
    });
    return Object.assign({}, info, { tCoreContent });
  };

  const introInfo = useMemo(() => {
    return translateData(data);
  }, [data, locale]);

  if (!introInfo) return null;

  const { tCoreContent } = introInfo || {};

  const intro = getCMSIntlVal(introInfo, 'intro', locale);
  // const intro = getCMSIntlVal(introInfo, 'intro', locale).replace(/\\n/g, '\n');
  const textLine = intro.replace(/\\n/g, '\n');
  const [text, ...resetText] = textLine.split(/\${COLLAPSE}/g);
  const introAll = textLine.replace(/\${COLLAPSE}/g, '');

  return (
    <SectionContainer title={title} titleClassName="mobile:mb-[12px]">
      <div className=" mb-[24px] ">
        <p className="text-[18px] leading-[2] text-[#121C59] opacity-80 mobile:text-[12px] font-300 break-words whitespace-pre-line">
          {!isMobile || showMore ? introAll : text}
        </p>
        {resetText.length > 0 && !showMore && isMobile && (
          <div
            className="text-sm text-[#121C59] opacity-80 flex items-center mt-3"
            onClick={() => setShowMore(true)}
          >
            {t('moreText')}
            <ChevronDownIcon size={16}></ChevronDownIcon>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-[12px] mobile:grid-cols-1">
        {tCoreContent.map((item, index) => {
          return (
            <FeaturePointItemMobile
              item={item}
              className="hidden mobile:block"
              key={index}
              {...item}
            ></FeaturePointItemMobile>
          );
        })}
        {tCoreContent.map((item, index) => {
          return (
            <FeaturePointItem
              item={item}
              className="mobile:hidden"
              key={index}
              {...item}
            ></FeaturePointItem>
          );
        })}
      </div>
    </SectionContainer>
  );
}
